import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import { Building, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAgency } from '@/auth/AgencyProvider';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * The agency switcher (change spec 2026-09-24/topbar, design 3). Only for someone with ACTIVE
 * memberships in more than one agency: a single-agency member sees the agency name, not a control
 * (from sm; below sm nothing, the name is in the drawer's header). The choice goes through
 * AgencyProvider.setAgency, which keeps it in localStorage (`coreway.agency`), and every data hook is
 * keyed to the current agency, so the screens re-read. The portal returns to the dashboard: a load or
 * a thread of the previous agency would only answer "not found" in the new one.
 */
export function AgencySwitcher() {
  const { t } = useTranslation();
  const agency = useAgency();
  const navigate = useNavigate();

  if (agency.status !== 'ready') {
    return (
      <span className="hidden h-touch max-w-48 items-center truncate px-2 text-base text-ink-muted sm:flex">
        {agency.status === 'loading' ? '' : t('topbar.noWorkspace')}
      </span>
    );
  }

  const current = agency.agency;
  if (agency.memberships.length < 2) {
    return (
      <span className="hidden h-touch items-center gap-2 px-2 text-base text-ink-secondary sm:flex" title={current.name}>
        <Building className="size-5 shrink-0 text-ink-muted" aria-hidden="true" />
        <span className="max-w-48 truncate">{current.name}</span>
      </span>
    );
  }

  const choose = (id: string) => {
    if (id === current.id) return;
    const next = agency.memberships.find((m) => m.agencyId === id);
    agency.setAgency(id);
    navigate('/dashboard');
    if (next) toast(t('topbar.switched', { agency: next.agencyName }));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('topbar.switchAgency', { agency: current.name })}
          className="flex h-touch shrink-0 items-center gap-2 rounded-md border border-hairline px-2 text-base text-ink-secondary hover:bg-secondary sm:px-3"
        >
          <Building className="size-5 shrink-0 text-ink-muted" aria-hidden="true" />
          <span className="hidden max-w-40 truncate sm:inline lg:max-w-56" title={current.name}>
            {current.name}
          </span>
          <span className="sr-only sm:hidden">{current.name}</span>
          <ChevronDown className="hidden size-4 text-ink-muted sm:block" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(18rem,calc(100vw-2rem))]">
        <DropdownMenuLabel>{t('topbar.agencies')}</DropdownMenuLabel>
        <DropdownMenuPrimitive.RadioGroup value={current.id} onValueChange={choose}>
          {agency.memberships.map((m) => (
            <DropdownMenuPrimitive.RadioItem
              key={m.agencyId}
              value={m.agencyId}
              className={cn(
                'relative flex min-h-9 cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-base text-ink outline-none select-none',
                'focus:bg-secondary',
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{m.agencyName}</span>
                <span className="text-xs text-ink-muted">{t(`roles.${m.role}`)}</span>
              </span>
              <DropdownMenuPrimitive.ItemIndicator>
                <Check className="size-4 text-accent-base" aria-hidden="true" />
              </DropdownMenuPrimitive.ItemIndicator>
            </DropdownMenuPrimitive.RadioItem>
          ))}
        </DropdownMenuPrimitive.RadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
